/**
 * Distribution helpers for the engine.
 *
 * normalQuantile is Peter Acklam's rational approximation to the inverse
 * standard normal CDF (absolute error below about 1.2e-9 over the full
 * range), which is far inside the tolerances the brief sets for interval
 * endpoints (brief 14.1). For a 95% interval it returns 1.959964 when
 * rounded to six decimals, matching the value the brief prints in 9.1.
 *
 * Form of the approximation (Acklam's reference implementation): with
 * poly(coeffs, x) the Horner evaluation,
 *   central region:  z = q * polyA(r) / (r * polyB(r) + 1),  r = q^2
 *   tails:           z = +/- polyC(s) / (s * polyD(s) + 1), s = sqrt(-2 ln p)
 * with the sign negative in the lower tail and positive in the upper.
 */
const ACKLAM_A = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
    1.38357751867269e2, -3.066479806614716e1, 2.506628277459239,
];
const ACKLAM_B = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
    6.680131188771972e1, -1.328068155288572e1,
];
const ACKLAM_C = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783,
];
const ACKLAM_D = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
    3.754408661907416,
];
const P_LOW = 0.02425;
const P_HIGH = 1 - P_LOW;
function poly(coeffs, x) {
    let acc = 0;
    for (const c of coeffs)
        acc = acc * x + c;
    return acc;
}
/** Inverse standard normal CDF: returns z with Phi(z) = p, for 0 < p < 1. */
export function normalQuantile(p) {
    if (!(p > 0 && p < 1)) {
        throw new Error(`normalQuantile: p must lie in (0, 1); received ${p}.`);
    }
    if (p < P_LOW) {
        const s = Math.sqrt(-2 * Math.log(p));
        return poly(ACKLAM_C, s) / (s * poly(ACKLAM_D, s) + 1);
    }
    if (p <= P_HIGH) {
        const q = p - 0.5;
        const r = q * q;
        return (poly(ACKLAM_A, r) * q) / (r * poly(ACKLAM_B, r) + 1);
    }
    const s = Math.sqrt(-2 * Math.log(1 - p));
    return -poly(ACKLAM_C, s) / (s * poly(ACKLAM_D, s) + 1);
}
//# sourceMappingURL=distributions.js.map